import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaArrowLeft, FaCheckCircle, FaClock, FaStar, FaTruck, FaMapMarkerAlt, FaEuroSign, FaComment, FaPaperPlane, FaFileUpload, FaFilePdf, FaImage, FaDownload, FaFileInvoiceDollar } from 'react-icons/fa';

export default function ProjectDetail() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [generatingPO, setGeneratingPO] = useState(false);

  // Chat State
  const [showChatModal, setShowChatModal] = useState(false);
  const [activeChatSession, setActiveChatSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const chatEndRef = useRef(null);

  const fetchProjectData = async () => {
    try {
      const [projectRes, quotesRes] = await Promise.all([
        axios.get(`${API}/projects/my-projects/${projectId}?user_id=${user.id}`),
        axios.get(`${API}/projects/quotes/${projectId}?user_id=${user.id}`)
      ]);
      
      setProject(projectRes.data);
      setQuotes(quotesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!activeChatSession) return;
    try {
      const response = await axios.get(`${API}/projects/chat/messages/${activeChatSession.id}?user_id=${user.id}`);
      setChatMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  useEffect(() => {
    if (user && projectId) {
      fetchProjectData();
    }
  }, [user, projectId, fetchProjectData]);

  // Poll for messages when modal is open
  useEffect(() => {
    let interval;
    if (showChatModal && activeChatSession) {
      fetchMessages();
      interval = setInterval(fetchMessages, 5000);
    }
    return () => clearInterval(interval);
  }, [showChatModal, activeChatSession, fetchMessages]);

  // Scroll to bottom
  useEffect(() => {
    if (showChatModal) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showChatModal]);

  const handleAcceptQuote = async (quoteId) => {
    if (!window.confirm(i18n.language === 'fr' ? 'Valider ce devis ?' : 'Accept this quote?')) return;
    
    try {
      await axios.post(`${API}/projects/quotes/${quoteId}/accept?user_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Devis accepté' : 'Quote accepted');
      fetchProjectData(); // Refresh to show status
    } catch (error) {
      console.error('Error accepting quote:', error);
      toast.error('Erreur');
    }
  };

  const handleGeneratePO = async () => {
    setGeneratingPO(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/po?user_id=${user.id}`, {
        responseType: 'blob'
      });
      
      // Download PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${project.project_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Bon de commande généré');
    } catch (error) {
      console.error('Error generating PO:', error);
      toast.error('Erreur lors de la génération. Avez-vous accepté des devis ?');
    } finally {
      setGeneratingPO(false);
    }
  };

  const handleCompareQuotes = async () => {
    if (quotes.length === 0) {
      alert(i18n.language === 'fr' ? 'Aucun devis disponible' : 'No quotes available');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/compare/${projectId}?user_id=${user.id}`);
      setComparison(response.data);
      setActiveTab('comparison');
      setLoading(false);
    } catch (error) {
      console.error('Error comparing quotes:', error);
      alert(i18n.language === 'fr' ? 'Erreur lors de la comparaison' : 'Error comparing quotes');
      setLoading(false);
    }
  };

  const openChat = async (supplierId) => {
    try {
      setShowChatModal(true);
      // Get or create session
      const response = await axios.get(`${API}/projects/chat/session/${projectId}/${supplierId}`);
      setActiveChatSession(response.data);
      // Messages will be fetched by useEffect
    } catch (error) {
      console.error('Error opening chat:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeChatSession) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/projects/chat/send/${activeChatSession.id}?user_id=${user.id}`, {
        content: newMessage
      });
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('Fichier trop volumineux (max 10MB)');
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadRes = await axios.post(`${API}/projects/chat/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileData = uploadRes.data;
      
      await axios.post(`${API}/projects/chat/send/${activeChatSession.id}?user_id=${user.id}`, {
        content: `Document partagé: ${file.name}`,
        attachments: [fileData]
      });
      
      fetchMessages();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement');
    } finally {
      setUploadingFile(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-200 text-gray-700', text: i18n.language === 'fr' ? 'Brouillon' : 'Draft' },
      published: { color: 'bg-blue-100 text-blue-700', text: i18n.language === 'fr' ? 'Publié' : 'Published' },
      quotes_received: { color: 'bg-[#635BFF]/10 text-[#5248d9]', text: i18n.language === 'fr' ? 'Devis reçus' : 'Quotes Received' },
      completed: { color: 'bg-purple-100 text-purple-700', text: i18n.language === 'fr' ? 'Terminé' : 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-700', text: i18n.language === 'fr' ? 'Annulé' : 'Cancelled' }
    };

    const badge = badges[status] || badges.draft;
    return <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>{badge.text}</span>;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#635BFF]';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540] mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{i18n.language === 'fr' ? 'Projet non trouvé' : 'Project not found'}</p>
      </div>
    );
  }

  const hasAcceptedQuotes = quotes.some(q => q.status === 'accepted');

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate('/user/projects')}
            className="flex items-center gap-2 text-[#0A2540] hover:underline"
          >
            <FaArrowLeft />
            {i18n.language === 'fr' ? 'Retour aux projets' : 'Back to projects'}
          </button>
          
          {hasAcceptedQuotes && (
            <button
              onClick={handleGeneratePO}
              disabled={generatingPO}
              className="bg-[#0A2540] text-white px-4 py-2 rounded-lg hover:bg-[#003a52] flex items-center gap-2 shadow-lg"
            >
              <FaFileInvoiceDollar />
              {generatingPO ? 'Génération...' : (i18n.language === 'fr' ? 'Générer Bon de Commande' : 'Generate PO')}
            </button>
          )}
        </div>

        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-4xl font-bold text-[#0A2540] mb-2">{project.project_name}</h1>
            <p className="text-gray-600 flex items-center gap-2">
              <FaMapMarkerAlt /> {project.location}
              {project.site_address && ` - ${project.site_address}`}
            </p>
          </div>
          {getStatusBadge(project.status)}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-300">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-6 py-3 font-semibold ${activeTab === 'details' ? 'border-b-2 border-[#635BFF] text-[#635BFF]' : 'text-gray-600'}`}
          >
            {i18n.language === 'fr' ? 'Détails' : 'Details'}
          </button>
          <button
            onClick={() => setActiveTab('quotes')}
            className={`px-6 py-3 font-semibold ${activeTab === 'quotes' ? 'border-b-2 border-[#635BFF] text-[#635BFF]' : 'text-gray-600'}`}
          >
            {i18n.language === 'fr' ? `Devis (${quotes.length})` : `Quotes (${quotes.length})`}
          </button>
          {quotes.length > 0 && (
            <button
              onClick={() => setActiveTab('comparison')}
              className={`px-6 py-3 font-semibold ${activeTab === 'comparison' ? 'border-b-2 border-[#635BFF] text-[#635BFF]' : 'text-gray-600'}`}
            >
              {i18n.language === 'fr' ? 'Comparaison AI' : 'AI Comparison'}
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.language === 'fr' ? 'Informations du projet' : 'Project Information'}</CardTitle>
              </CardHeader>
              <CardContent>
                {project.delivery_deadline && (
                  <p className="mb-3">
                    <strong><FaClock className="inline mr-2" />{i18n.language === 'fr' ? 'Échéance:' : 'Deadline:'}</strong> {project.delivery_deadline}
                  </p>
                )}
                {project.additional_notes && (
                  <div>
                    <strong>{i18n.language === 'fr' ? 'Notes additionnelles:' : 'Additional Notes:'}</strong>
                    <p className="text-gray-600 mt-2">{project.additional_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{i18n.language === 'fr' ? 'Fournitures nécessaires' : 'Required Supplies'}</CardTitle>
              </CardHeader>
              <CardContent>
                {project.supplies && project.supplies.length > 0 ? (
                  <div className="space-y-4">
                    {project.supplies.map((supply, index) => (
                      <div key={index} className="border-l-4 border-[#635BFF] bg-gray-50 p-4 rounded">
                        <h4 className="font-bold text-lg mb-1">{supply.name}</h4>
                        <p className="text-gray-600 mb-2">
                          <strong>{i18n.language === 'fr' ? 'Quantité:' : 'Quantity:'}</strong> {supply.quantity} {supply.unit}
                        </p>
                        {supply.description && (
                          <p className="text-gray-600 mb-1">
                            <strong>{i18n.language === 'fr' ? 'Description:' : 'Description:'}</strong> {supply.description}
                          </p>
                        )}
                        {supply.specifications && (
                          <p className="text-gray-600">
                            <strong>{i18n.language === 'fr' ? 'Spécifications:' : 'Specifications:'}</strong> {supply.specifications}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">{i18n.language === 'fr' ? 'Aucune fourniture' : 'No supplies'}</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'quotes' && (
          <div className="space-y-6">
            {quotes.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-600 mb-2">
                    {i18n.language === 'fr' 
                      ? 'Aucun devis reçu pour le moment' 
                      : 'No quotes received yet'}
                  </p>
                  {project.status === 'draft' && (
                    <p className="text-sm text-gray-500">
                      {i18n.language === 'fr' 
                        ? 'Publiez votre projet pour recevoir des devis' 
                        : 'Publish your project to receive quotes'}
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleCompareQuotes}
                    className="bg-[#635BFF] text-white px-6 py-3 rounded-lg hover:bg-[#5248d9] font-semibold"
                  >
                    {i18n.language === 'fr' ? '🤖 Comparer avec AI' : '🤖 Compare with AI'}
                  </button>
                </div>

                {quotes.map((quote) => (
                  <Card key={quote.id} className={`${quote.ai_recommended ? 'border-2 border-[#635BFF]' : ''} ${quote.status === 'accepted' ? 'bg-[#635BFF]/5' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex gap-2 items-start">
                          <div>
                            {quote.status === 'accepted' && (
                              <div className="bg-[#635BFF] text-white px-3 py-1 rounded-full inline-block mb-2 text-sm font-semibold">
                                ✓ {i18n.language === 'fr' ? 'Accepté' : 'Accepted'}
                              </div>
                            )}
                            {quote.ai_recommended && (
                              <div className="bg-[#635BFF] text-white px-3 py-1 rounded-full inline-block mb-2 text-sm font-semibold ml-2">
                                ⭐ {i18n.language === 'fr' ? 'Recommandé' : 'Recommended'}
                              </div>
                            )}
                            <h3 className="text-xl font-bold text-[#0A2540] mb-1">{quote.supplier_name}</h3>
                            {quote.supplier_location && (
                              <p className="text-gray-600 text-sm">
                                <FaMapMarkerAlt className="inline mr-1" />
                                {quote.supplier_location}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-[#635BFF]">{quote.total?.toFixed(2) || '0.00'}€</p>
                          {quote.delivery_time && (
                            <p className="text-sm text-gray-600 mb-2">
                              <FaTruck className="inline mr-1" />
                              {quote.delivery_time}
                            </p>
                          )}
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => openChat(quote.supplier_id)}
                              className="bg-[#0A2540] text-white px-4 py-2 rounded-lg hover:bg-[#003a52] text-sm flex items-center gap-2"
                            >
                              <FaComment /> {i18n.language === 'fr' ? 'Discuter' : 'Chat'}
                            </button>
                            {quote.status !== 'accepted' && (
                              <button
                                onClick={() => handleAcceptQuote(quote.id)}
                                className="bg-[#635BFF] text-white px-4 py-2 rounded-lg hover:bg-[#5248d9] text-sm flex items-center gap-2"
                              >
                                <FaCheckCircle /> {i18n.language === 'fr' ? 'Valider' : 'Accept'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* AI Scores */}
                      {quote.ai_score_overall && (
                        <div className="grid grid-cols-5 gap-3 mb-4">
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">{i18n.language === 'fr' ? 'Prix' : 'Price'}</p>
                            <p className={`text-lg font-bold ${getScoreColor(quote.ai_score_price)}`}>
                              {quote.ai_score_price}/100
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">{i18n.language === 'fr' ? 'Qualité' : 'Quality'}</p>
                            <p className={`text-lg font-bold ${getScoreColor(quote.ai_score_quality)}`}>
                              {quote.ai_score_quality}/100
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">{i18n.language === 'fr' ? 'Proximité' : 'Proximity'}</p>
                            <p className={`text-lg font-bold ${getScoreColor(quote.ai_score_proximity)}`}>
                              {quote.ai_score_proximity}/100
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">{i18n.language === 'fr' ? 'Livraison' : 'Delivery'}</p>
                            <p className={`text-lg font-bold ${getScoreColor(quote.ai_score_delivery)}`}>
                              {quote.ai_score_delivery}/100
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600 mb-1">{i18n.language === 'fr' ? 'Global' : 'Overall'}</p>
                            <p className={`text-lg font-bold ${getScoreColor(quote.ai_score_overall)}`}>
                              {quote.ai_score_overall}/100
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Line Items */}
                      {quote.line_items && quote.line_items.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold mb-2">{i18n.language === 'fr' ? 'Détails:' : 'Details:'}</h4>
                          <div className="bg-white border border-gray-200 p-3 rounded">
                            {quote.line_items.map((item, idx) => (
                              <div key={idx} className="flex justify-between py-1 text-sm border-b last:border-0">
                                <span>{item.supply_name} ({item.quantity})</span>
                                <span className="font-semibold">{item.total_price?.toFixed(2) || '0.00'}€</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Certifications */}
                      {quote.certifications && quote.certifications.length > 0 && (
                        <div className="mb-3">
                          <h4 className="font-semibold mb-2">{i18n.language === 'fr' ? 'Certifications:' : 'Certifications:'}</h4>
                          <div className="flex gap-2 flex-wrap">
                            {quote.certifications.map((cert, idx) => (
                              <span key={idx} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                                <FaCheckCircle className="inline mr-1" />
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quality Notes */}
                      {quote.quality_notes && (
                        <div>
                          <h4 className="font-semibold mb-1">{i18n.language === 'fr' ? 'Notes qualité:' : 'Quality Notes:'}</h4>
                          <p className="text-gray-600 text-sm">{quote.quality_notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </div>
        )}

        {activeTab === 'comparison' && (
          <div className="space-y-6">
            {!comparison ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-600 mb-4">
                    {i18n.language === 'fr' 
                      ? 'Cliquez sur "Comparer avec AI" pour obtenir une analyse détaillée' 
                      : 'Click "Compare with AI" to get a detailed analysis'}
                  </p>
                  <button
                    onClick={handleCompareQuotes}
                    className="bg-[#635BFF] text-white px-6 py-3 rounded-lg hover:bg-[#5248d9] font-semibold"
                  >
                    {i18n.language === 'fr' ? '🤖 Lancer l\'analyse' : '🤖 Start Analysis'}
                  </button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* AI Analysis */}
                <Card className="border-2 border-[#635BFF]">
                  <CardHeader className="bg-gradient-to-r from-[#635BFF] to-[#5248d9] text-white">
                    <CardTitle className="flex items-center gap-2">
                      🤖 {i18n.language === 'fr' ? 'Analyse Intelligente GPT-5' : 'GPT-5 Intelligent Analysis'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {comparison.ai_analysis ? (
                      <div className="prose max-w-none">
                        <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                          {comparison.ai_analysis}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-600">
                        {i18n.language === 'fr' 
                          ? 'Analyse non disponible' 
                          : 'Analysis not available'}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Best Quotes Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>{i18n.language === 'fr' ? 'Résumé des meilleurs devis' : 'Best Quotes Summary'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {comparison.best_price_quote && (
                        <div className="text-center p-4 bg-[#635BFF]/5 rounded-lg">
                          <FaEuroSign className="text-3xl text-[#635BFF] mx-auto mb-2" />
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'fr' ? 'Meilleur prix' : 'Best Price'}</p>
                          <p className="font-bold text-[#635BFF]">
                            {comparison.quotes.find(q => q.id === comparison.best_price_quote)?.supplier_name}
                          </p>
                        </div>
                      )}
                      {comparison.best_quality_quote && (
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <FaStar className="text-3xl text-blue-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'fr' ? 'Meilleure qualité' : 'Best Quality'}</p>
                          <p className="font-bold text-blue-600">
                            {comparison.quotes.find(q => q.id === comparison.best_quality_quote)?.supplier_name}
                          </p>
                        </div>
                      )}
                      {comparison.best_delivery_quote && (
                        <div className="text-center p-4 bg-yellow-50 rounded-lg">
                          <FaTruck className="text-3xl text-yellow-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'fr' ? 'Livraison rapide' : 'Fast Delivery'}</p>
                          <p className="font-bold text-yellow-600">
                            {comparison.quotes.find(q => q.id === comparison.best_delivery_quote)?.supplier_name}
                          </p>
                        </div>
                      )}
                      {comparison.ai_recommended_quote && (
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                          <FaCheckCircle className="text-3xl text-purple-600 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 mb-1">{i18n.language === 'fr' ? 'Recommandé' : 'Recommended'}</p>
                          <p className="font-bold text-purple-600">
                            {comparison.quotes.find(q => q.id === comparison.ai_recommended_quote)?.supplier_name}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Chat Modal */}
        {showChatModal && activeChatSession && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full h-[600px] flex flex-col shadow-2xl">
              {/* Header */}
              <div className="p-4 border-b bg-[#0A2540] text-white rounded-t-lg flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <FaComment />
                    {activeChatSession.supplier_name}
                  </h3>
                  <p className="text-xs text-blue-100">
                    {project.project_name}
                  </p>
                </div>
                <button 
                  onClick={() => setShowChatModal(false)}
                  className="text-white hover:text-gray-200 text-xl font-bold"
                >
                  ×
                </button>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
                {chatMessages.length === 0 ? (
                  <div className="text-center text-gray-400 mt-10">
                    <p>Commencez la discussion...</p>
                  </div>
                ) : (
                  chatMessages.map((msg) => {
                    const isMe = msg.sender_id === user.id;
                    return (
                      <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg p-3 ${
                          isMe ? 'bg-[#0A2540] text-white' : 'bg-white border border-gray-200 shadow-sm'
                        }`}>
                          {!isMe && <p className="text-xs font-bold text-[#0A2540] mb-1">{msg.sender_name}</p>}
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {msg.attachments.map((file, idx) => (
                                <a 
                                  key={idx}
                                  href={`${API}${file.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`block text-xs p-2 rounded flex items-center gap-2 ${
                                    isMe ? 'bg-[#003a52] hover:bg-[#002d40]' : 'bg-gray-100 hover:bg-gray-200'
                                  }`}
                                >
                                  {file.type === 'pdf' ? <FaFilePdf className="text-red-400" /> : <FaImage className="text-blue-400" />}
                                  <span className="truncate flex-1">{file.filename}</span>
                                  <FaDownload />
                                </a>
                              ))}
                            </div>
                          )}
                          
                          <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t bg-white rounded-b-lg">
                <div className="flex gap-2 items-end">
                  <label className="cursor-pointer text-gray-500 hover:text-[#0A2540] p-2">
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      className="hidden"
                    />
                    <FaFileUpload size={20} />
                  </label>
                  
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Message..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm max-h-24 resize-none"
                    rows="1"
                  />
                  
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="bg-[#635BFF] text-white p-2 rounded-full hover:bg-[#5248d9] disabled:opacity-50"
                  >
                    <FaPaperPlane size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
